import { forwardRef, useImperativeHandle, useRef, type ReactNode, type CSSProperties } from 'react';
import { cn } from '../../lib/cn';
import { SIcon, type SIconName } from '../SIcon';
import { STooltip, type STooltipProps } from '../STooltip';
import { type SColor } from '../../lib/color';
import { type Rule } from '../../lib/form';

export type SFieldSize = 'sm' | 'md';
export type SFieldAddonAlign = 'start' | 'center' | 'end';
export type SFieldStatus = 'default' | 'pass' | 'error';

// fieldGap: label↔control 간격 (시스템 --sys-space-field-{size}-gap: 12/16)
// labelGap: label 내부 아이콘·툴팁 간격 (컴포넌트 --cmp-field-label-{size}-gap: 6/8)
const FIELD_SIZE: Record<
  SFieldSize,
  {
    height: number;
    radius: number;
    fieldGap: number;
    labelGap: number;
    fontSize: number;
    lineHeight: number;
    icon: number;
  }
> = {
  sm: { height: 28, radius: 4, fieldGap: 12, labelGap: 6, fontSize: 12, lineHeight: 20, icon: 16 },
  md: { height: 36, radius: 6, fieldGap: 16, labelGap: 8, fontSize: 14, lineHeight: 24, icon: 16 },
};

const ALIGN: Record<SFieldAddonAlign, CSSProperties['justifyContent']> = {
  start: 'flex-start',
  center: 'center',
  end: 'flex-end',
};

/** ref로 노출되는 UX 메서드 (sd-field @Method sdFocus 대응) */
export interface SFieldHandle {
  /** 내부 컨트롤에 포커스하고 필드를 화면에 스크롤합니다. */
  focus: () => void;
}

export interface SFieldProps {
  /** 폼 연동용 name 속성 */
  name?: string;
  /** 레이블 텍스트 */
  label?: string;
  /** 레이블 영역 아이콘명 (크기는 size 토큰 16px 고정) */
  icon?: SIconName;
  /** 레이블 영역 아이콘 색상. 미지정 시 currentColor 상속 */
  iconColor?: SColor;
  /** 레이블 툴팁 텍스트 */
  labelTooltip?: string;
  /** 레이블 툴팁 상세 옵션 (SdTooltipProps 대응) */
  labelTooltipProps?: Partial<STooltipProps>;
  /** 레이블/어드온 레이블 너비 (숫자=px). label·addonLabel 폭에 공통 적용 */
  labelWidth?: number | string;
  /**
   * 레이블 박스 높이 (숫자=px). 미지정 시 size별 기본값 사용.
   * 컨트롤 박스가 기본 필드 높이보다 큰 경우(multiline 등) 첫 행에 세로 중앙 정렬을
   * 맞추기 위해 컨트롤 박스 높이를 넘겨받는다.
   */
  labelHeight?: number | string;
  /** 필드 크기 */
  size?: SFieldSize;
  /** 우측 어드온 레이블 */
  addonLabel?: string;
  /** 어드온 정렬 */
  addonAlign?: SFieldAddonAlign;
  /** 에러 상태 */
  error?: boolean;
  /** 에러 메시지 */
  errorMessage?: string;
  /** 필드 상태 — 'error' | 'pass' | 'default'. 'pass'면 초록 테두리 */
  status?: SFieldStatus;
  /** 포커스 상태 (제어/반영) */
  focused?: boolean;
  /** 호버 상태 (제어/반영) */
  hovered?: boolean;
  /** 유효성 규칙 (검증은 컨트롤에서 수행, API 패리티) */
  rules?: Rule[];
  /** 하단 힌트 */
  hint?: string;
  /** 비활성 */
  disabled?: boolean;
  /** 읽기 전용 (회색 배경) */
  readOnly?: boolean;
  /**
   * 컨트롤 너비 (숫자=px). 지정하면 필드가 부모 폭을 다 먹지 않고 (레이블 + width) 만큼만 차지한다.
   * 다른 요소와 나란히 놓으려면 부모를 flex 로 두면 된다.
   */
  width?: number | string;
  /** 컨트롤 최소 너비 (숫자=px). 하한선만 지정하며 필드는 계속 부모 폭을 채운다 */
  minWidth?: number | string;
  /** 멀티라인(textarea) — 컨트롤 높이를 고정하지 않고 min-height만 적용 */
  multiline?: boolean;
  /**
   * 테두리 박스 제거 (inline 컨트롤용) — border/배경/hover·focus 강조만 사라지고
   * label·hint·errorMessage 등 나머지 필드 구성은 그대로 동작한다.
   */
  borderless?: boolean;
  /** 실제 컨트롤 (input/select 등) — 테두리 없이 렌더, 테두리는 SField가 제공 */
  children?: ReactNode;
  className?: string;
  style?: CSSProperties;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}

const cssLen = (v?: number | string) =>
  v === undefined || v === '' || v === 0 ? undefined : typeof v === 'number' ? `${v}px` : v;

/**
 * SField — sd-field 포팅. 폼 컨트롤 래퍼.
 * ★ SField 가 컨트롤 테두리 박스(border/radius/bg + hover/focus/error/disabled 상태)를 소유하고,
 *   children(input/select 등)은 테두리 없이 그 안을 채운다. (Stencil sd-field 구조)
 */
export const SField = forwardRef<SFieldHandle, SFieldProps>(function SField(
  {
    name,
    label = '',
    icon,
    iconColor,
    labelTooltip = '',
    labelTooltipProps,
    labelWidth,
    labelHeight,
    size = 'sm',
    addonLabel = '',
    addonAlign = 'start',
    error = false,
    errorMessage = '',
    status,
    focused = false,
    hovered = false,
    rules: _rules,
    hint = '',
    disabled = false,
    readOnly = false,
    width,
    minWidth,
    multiline = false,
    borderless = false,
    children,
    className,
    style,
    onMouseEnter,
    onMouseLeave,
  },
  ref,
) {
  const rootRef = useRef<HTMLDivElement>(null);
  useImperativeHandle(
    ref,
    () => ({
      focus: () => {
        const root = rootRef.current;
        if (!root) return;
        const focusable = root.querySelector<HTMLElement>(
          'input,textarea,select,button,[tabindex]',
        );
        focusable?.focus();
        root.scrollIntoView({ block: 'nearest' });
      },
    }),
    [],
  );

  const s = FIELD_SIZE[size];
  // label 과 addonLabel 은 함께 사용 불가 — 둘 다 지정 시 label 우선, addonLabel 무시
  if (label !== '' && addonLabel !== '' && typeof console !== 'undefined') {
    console.warn(
      '[SField] label 과 addonLabel 은 함께 사용할 수 없습니다. addonLabel 을 무시합니다.',
    );
  }
  const effectiveAddonLabel = label !== '' ? '' : addonLabel;
  // status가 있으면 error보다 우선 반영 (원본: status !== undefined 시 override)
  const resolvedError = status === 'error' || (status === undefined && error);
  const resolvedSuccess = status === 'pass';
  const showError = resolvedError && errorMessage !== '';
  // hover/focus 는 error/pass 보다 우선 (원본 sd-field: error/pass 는 :not(:hover), hover/focus 는 focus색 !important).
  // disabled/readOnly 만 hover/focus 강조에서 제외.
  const interactive = !disabled && !readOnly;
  // width 가 지정되면 루트가 부모 폭을 다 먹지 않고 (label + width) 만큼만 hug 한다.
  // minWidth 는 하한선 용도로 항상 넘어오는 경우가 있어(STimePicker 등) hug 판정에서 제외.
  const hug = cssLen(width) !== undefined;

  const borderColor = borderless
    ? 'transparent'
    : disabled
      ? 'var(--color-grey-45)'
      : interactive && focused
        ? 'var(--sys-color-field-border-focus)' // focus(제어 prop) 가 error/pass 보다 우선
        : resolvedSuccess
          ? 'var(--sys-color-field-border-success)'
          : resolvedError
            ? 'var(--sys-color-field-border-danger)'
            : 'var(--sys-color-field-border-default)';
  const bgColor = borderless
    ? 'transparent'
    : disabled
      ? 'var(--color-grey-30)'
      : readOnly
        ? 'var(--sys-color-field-bg-readOnly)'
        : 'var(--sys-color-field-bg-default)';
  const textColor = disabled
    ? 'var(--color-grey-65)'
    : readOnly
      ? 'var(--sys-color-field-text-readOnly)'
      : 'var(--sys-color-field-text-default)';

  return (
    <div
      ref={rootRef}
      data-name={name || undefined}
      className={cn(
        'h-fit flex-col',
        // hug 시 inline-flex 를 유지하면 기존에 세로로 쌓여 있던 필드들이 한 줄에 붙어버리므로
        // 블록 레벨(flex)로 두어 쌓임은 그대로 유지한다. 나란히 놓으려면 부모를 flex 로.
        hug ? 'flex w-fit max-w-full' : 'inline-flex w-full',
        className,
      )}
      style={style}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div className="flex w-full items-start">
        {label !== '' && (
          <div
            className="flex shrink-0 items-center font-bold text-(--sys-color-field-text-default)"
            style={{
              height: cssLen(labelHeight) ?? s.height,
              marginRight: s.fieldGap,
              width: cssLen(labelWidth),
              fontSize: s.fontSize,
              lineHeight: `${s.lineHeight}px`,
            }}
          >
            {icon && (
              <SIcon
                name={icon}
                size={s.icon}
                color={iconColor}
                style={{ marginRight: s.labelGap }}
              />
            )}
            <span className="min-w-0 overflow-hidden text-ellipsis whitespace-nowrap">{label}</span>
            {(labelTooltip !== '' || labelTooltipProps) && (
              <span className="shrink-0 flex items-center" style={{ marginLeft: s.labelGap }}>
                <STooltip
                  message={labelTooltip !== '' ? [labelTooltip] : undefined}
                  iconSize={s.icon}
                  {...labelTooltipProps}
                />
              </span>
            )}
          </div>
        )}

        <div
          // 폭을 control 이 아니라 이 래퍼에 걸어야 hint/errorMessage 도 같은 폭에서 줄바꿈된다.
          className={cn('flex min-w-0 flex-col', !hug && 'flex-1')}
          style={{ width: cssLen(width), minWidth: cssLen(minWidth) }}
        >
          {/* 테두리 박스 = 컨트롤 */}
          <div
            className={cn(
              'relative box-border flex min-w-0 items-stretch overflow-hidden border border-solid border-(--field-bc)',
              interactive &&
                !borderless &&
                'hover:border-(--sys-color-field-border-focus) hover:shadow-[0px_0px_4px_0px_#0071ff66] focus-within:border-(--sys-color-field-border-focus) focus-within:shadow-[0px_0px_4px_0px_#0071ff66]',
              disabled && 'cursor-not-allowed',
            )}
            style={{
              // @ts-expect-error CSS 변수
              '--field-bc': borderColor,
              // focused/hovered prop이 명시된 경우 강조를 즉시 반영
              ...(interactive && !borderless && focused
                ? { boxShadow: '0px 0px 4px 0px #0071ff66' }
                : {}),
              ...(multiline ? { minHeight: s.height } : { height: s.height }),
              'borderRadius': s.radius,
              'background': bgColor,
              'color': textColor,
            }}
            data-focused={focused || undefined}
            data-hovered={hovered || undefined}
          >
            {effectiveAddonLabel !== '' && (
              <div
                className="flex shrink-0 items-center gap-(--cmp-field-addonLabel-gap) whitespace-nowrap border-r border-solid px-(--cmp-field-addonLabel-paddingX) text-12 font-medium leading-sd-20"
                style={{
                  // 원본 sd-field: labelWidth가 addon 폭(--sd-field-addon-width)에도 적용됨
                  width: cssLen(labelWidth),
                  justifyContent: ALIGN[addonAlign],
                  background: 'var(--cmp-field-addonLabel-bg)',
                  borderRightColor: disabled
                    ? 'var(--cmp-field-addonLabel-border-disabled)'
                    : 'var(--cmp-field-addonLabel-border-default)',
                  borderTopLeftRadius: s.radius - 1,
                  borderBottomLeftRadius: s.radius - 1,
                }}
              >
                {icon && !label && <SIcon name={icon} size={s.icon} color={iconColor} />}
                {effectiveAddonLabel}
              </div>
            )}
            <div className="flex min-w-0 flex-1">{children}</div>
          </div>

          {showError ? (
            <div className="mt-(--cmp-field-contents-gap) px-(--cmp-field-message-paddingX) text-12 font-normal leading-sd-20 text-(--cmp-field-errorMessage-color)">
              {errorMessage}
            </div>
          ) : hint !== '' ? (
            <div className="mt-(--cmp-field-contents-gap) px-(--cmp-field-message-paddingX) text-12 font-normal leading-sd-20 text-(--cmp-field-hint-color)">
              {hint}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
});
